import React from 'react';
import {
  ReorderRuleExportConfigurationContext,
  ReorderRuleExportConfigurationContextProps,
} from '../context/ReorderRuleExportConfigurationContext';
import { ReorderRuleExportConfiguration } from '../types/reorder_rule_export_configuration/reorderRuleExportConfiguration';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';

const reorderRuleExportConfigurationState = {
  items: new Map<string, ReorderRuleExportConfiguration>(),
  loading: false,
};

interface ReorderRuleExportConfigurationState {
  key: string;
}

export const reorderRuleExportConfigurationSlice = createSlice({
  name: 'reorderRuleExportConfiguration',
  initialState: { key: v4() } as ReorderRuleExportConfigurationState,
  reducers: {
    reset: state => {
      reorderRuleExportConfigurationState.items = new Map();
      reorderRuleExportConfigurationState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<ReorderRuleExportConfiguration[]>) => {
      reorderRuleExportConfigurationState.items = new Map();
      action.payload.forEach(c =>
        reorderRuleExportConfigurationState.items.set(c.id, new ReorderRuleExportConfiguration(c)),
      );
      state.key = v4();
    },
    add: (state, action: PayloadAction<ReorderRuleExportConfiguration[]>) => {
      action.payload.forEach(c =>
        reorderRuleExportConfigurationState.items.set(c.id, new ReorderRuleExportConfiguration(c)),
      );
      reorderRuleExportConfigurationState.items = new Map(reorderRuleExportConfigurationState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<ReorderRuleExportConfiguration[]>) => {
      action.payload.forEach(c => reorderRuleExportConfigurationState.items.delete(c.id));
      reorderRuleExportConfigurationState.items = new Map(reorderRuleExportConfigurationState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      reorderRuleExportConfigurationState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface ReorderRuleExportConfigurationProviderProps {
  children: React.ReactNode;
}

export default function ReorderRuleExportConfigurationProvider({
  children,
}: ReorderRuleExportConfigurationProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.reorderRuleExportConfigurations.key);

  const reorderRuleExportConfigurationContext: ReorderRuleExportConfigurationContextProps = {
    reorderRuleExportConfigurations: reorderRuleExportConfigurationState.items,
    reorderRuleExportConfigurationsLoading: reorderRuleExportConfigurationState.loading,
    setReorderRuleExportConfigurations: (
      reorderRuleExportConfigurations: Map<string, ReorderRuleExportConfiguration>,
    ) => dispatch(reorderRuleExportConfigurationSlice.actions.set([...reorderRuleExportConfigurations.values()])),
  };

  return (
    <ReorderRuleExportConfigurationContext.Provider value={reorderRuleExportConfigurationContext}>
      {children}
    </ReorderRuleExportConfigurationContext.Provider>
  );
}
